/**
 * PURPOSE: Pestaña "Uniformes" de Administración General (FTR-0014; modelo original restaurado 2026-09-14):
 *   matriz Winscore de opciones normalizadas con hasta 3 alternativas ordenadas por tipo y tono, y foto.
 * RESPONSIBILITIES: Elegir evento y deporte de la compañía efectiva; mostrar por equipo las columnas que la
 *   política del deporte permite, cada una con hasta 3 alternativas ordenadas (opción / registro legacy sin
 *   normalizar / vacío) con contador "n/3", botón de alta contextual ("+ Agregar camiseta clara" / "+
 *   Agregar otra camiseta clara") y "Máximo 3" al llenarse; estado del equipo (Completo con al menos una
 *   Camiseta activa, en cualquier tono — Arquero/Líbero son adicionales y opcionales), con legacy y plazo;
 *   crear, editar, eliminar, reordenar y reemplazar registros legacy; panel de plazos en hora de Lima. Roles
 *   1/4 por la frontera FTR-0012 del backend.
 * COLLABORATORS: uniformOptionsService.js, uniformOptions.utils.js, UniformOptionEditor.jsx,
 *   UniformDeadlinePanel.jsx, UniformOptionVisual.jsx, FloatingHorizontalScrollbar.jsx, AdminCompanySection.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/CompanyUniformsTab.jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiArrowDown, FiArrowUp, FiEdit2, FiPlus, FiRefreshCw, FiTrash2 } from "react-icons/fi";

import FloatingHorizontalScrollbar from "../../components/FloatingHorizontalScrollbar/FloatingHorizontalScrollbar.jsx";
import {
  createUniformOption,
  deleteUniformOption,
  getUniformMatrix,
  getUniformPolicies,
  listUniformEventSports,
  listUniformEvents,
  reorderUniformOptions,
  updateUniformOption,
  uploadUniformOptionPhoto,
} from "../../services/uniformOptionsService.js";
import UniformDeadlinePanel from "./UniformDeadlinePanel.jsx";
import UniformOptionEditor from "./UniformOptionEditor.jsx";
import UniformOptionVisual from "./UniformOptionVisual.jsx";
import {
  MAX_OPTIONS_PER_TYPE_TONE,
  TEAM_STATUS,
  addOptionButtonLabel,
  buildCreatePayload,
  buildUpdatePayload,
  describeDeadline,
  getUniformErrorMessage,
  moveOptionInColumn,
  optionPositionLabel,
  primaryOptionLabel,
  sectionLabel,
} from "./uniformOptions.utils.js";

const RELOAD_CODES = new Set(["UNIFORM_SLOT_CONFLICT", "REORDER_MISMATCH", "UNIFORM_OPTION_NOT_FOUND"]);
const BADGE_TONES = {
  emerald: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  rose: "bg-rose-100 text-rose-800",
  slate: "bg-slate-100 text-slate-700",
};
const STATUS_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "MISSING", label: "Sin registrar" },
  { id: "INCOMPLETE", label: "Incompletos" },
  { id: "COMPLETE", label: "Completos" },
  { id: "LEGACY", label: "Con legacy" },
];

const Badge = ({ tone = "slate", children, title }) => (
  <span title={title} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${BADGE_TONES[tone] || BADGE_TONES.slate}`}>
    {children}
  </span>
);

const IconButton = ({ label, onClick, disabled, children, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    className={`rounded p-1 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 ${danger ? "hover:text-rose-600" : "hover:text-slate-800"}`}
  >
    {children}
  </button>
);

const SlotCell = ({ team, column, slot, isFirstEmpty, hasAnyOption, busy, onCreate, onEdit, onDelete, onReplace, onMove }) => {
  if (slot.kind === "OPTION") {
    const { option } = slot;
    const optionIds = column.slots.filter((item) => item.kind === "OPTION").map((item) => item.option.id);
    const position = optionIds.indexOf(option.id);
    const label = primaryOptionLabel(option);
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1">
        {/* Foto Y swatches: la foto nunca reemplaza los colores (corrección 2026-09-14). */}
        <UniformOptionVisual option={option} size="sm" swatchSize="sm" alt={label} />
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="max-w-[8rem] truncate text-xs font-semibold text-slate-800" title={label}>
            {label}
          </span>
          <span className="text-[10px] text-slate-400">{optionPositionLabel(slot.order)}</span>
        </div>
        <span className="ml-auto flex items-center">
          <IconButton label="Subir" disabled={busy || position <= 0} onClick={() => onMove(team, column, option, -1)}><FiArrowUp /></IconButton>
          <IconButton label="Bajar" disabled={busy || position < 0 || position >= optionIds.length - 1} onClick={() => onMove(team, column, option, 1)}><FiArrowDown /></IconButton>
          <IconButton label="Editar" disabled={busy} onClick={() => onEdit(team, column, option)}><FiEdit2 /></IconButton>
          <IconButton label="Eliminar" disabled={busy} danger onClick={() => onDelete(team, option)}><FiTrash2 /></IconButton>
        </span>
      </div>
    );
  }
  if (slot.kind === "LEGACY") {
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-2 py-1">
        <span className="max-w-[8rem] truncate text-xs italic text-amber-900" title="Registro legacy sin normalizar">
          {slot.legacy.label || "Sin texto"}
        </span>
        <Badge tone="amber">Legacy</Badge>
        <button type="button" disabled={busy} onClick={() => onReplace(team, column, slot.legacy)} className="ml-auto text-[11px] font-semibold text-blue-700 hover:underline disabled:opacity-40">
          Reemplazar
        </button>
      </div>
    );
  }
  if (!isFirstEmpty) return <div className="px-2 py-1 text-xs text-slate-300">—</div>;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onCreate(team, column)}
      className="flex w-full items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2 py-1 text-xs font-semibold text-slate-500 hover:border-blue-400 hover:text-blue-700 disabled:opacity-40"
    >
      <FiPlus /> {addOptionButtonLabel(column, hasAnyOption)}
    </button>
  );
};

const CompanyUniformsTab = ({ idcompany }) => {
  const [events, setEvents] = useState([]);
  const [idevent, setIdevent] = useState("");
  const [sports, setSports] = useState([]);
  const [idsport, setIdsport] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState("");
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const matrixScrollRef = useRef(null);

  const handleLoadError = useCallback((error, fallback) => {
    if (error?.response?.data?.code === "UNIFORM_OPTIONS_DISABLED") {
      setUnavailable("Las opciones de uniforme normalizadas no están habilitadas en este entorno.");
      return;
    }
    toast.error(getUniformErrorMessage(error, fallback));
  }, []);

  useEffect(() => {
    listUniformEvents()
      .then(setEvents)
      .catch((error) => handleLoadError(error, "No se pudieron cargar los eventos."));
  }, [idcompany, handleLoadError]);

  useEffect(() => {
    setSports([]);
    setIdsport("");
    setMatrix(null);
    setSummary(null);
    if (!idevent) return;
    listUniformEventSports(idevent)
      .then((items) => {
        setSports(items);
        if (items.length === 1) setIdsport(String(items[0].id));
      })
      .catch((error) => handleLoadError(error, "No se pudieron cargar los deportes del evento."));
    getUniformPolicies(idevent)
      .then(setSummary)
      .catch((error) => handleLoadError(error, "No se pudieron cargar los plazos."));
  }, [idevent, handleLoadError]);

  const loadMatrix = useCallback(async () => {
    if (!idevent || !idsport) return;
    setLoading(true);
    try {
      setMatrix(await getUniformMatrix({ idevent, idsport }));
    } catch (error) {
      setMatrix(null);
      handleLoadError(error, "No se pudo cargar la matriz de uniformes.");
    } finally {
      setLoading(false);
    }
  }, [handleLoadError, idevent, idsport]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const mergeTeamState = (state) => {
    if (!state?.team) return;
    setMatrix((current) =>
      current
        ? {
            ...current,
            teams: current.teams.map((team) =>
              team.idparticipant === state.team.idparticipant
                ? { ...team, columns: state.columns, legacyOutsidePolicy: state.legacyOutsidePolicy, status: state.status, hasLegacy: state.hasLegacy, deadline: state.deadline }
                : team
            ),
          }
        : current
    );
  };

  /** Refleja en la matriz una opción cuya foto cambió, sin recargar toda la fila del equipo. */
  const mergeOptionPhoto = (idparticipant, updatedOption) => {
    if (!updatedOption) return;
    setMatrix((current) =>
      current
        ? {
            ...current,
            teams: current.teams.map((team) =>
              team.idparticipant !== idparticipant
                ? team
                : {
                    ...team,
                    columns: team.columns.map((column) => ({
                      ...column,
                      slots: column.slots.map((slot) =>
                        slot.kind === "OPTION" && slot.option.id === updatedOption.id ? { ...slot, option: updatedOption } : slot
                      ),
                    })),
                  }
            ),
          }
        : current
    );
  };

  /** Devuelve el resultado del backend (verdadero) o null si falló, para poder encadenar la foto de un alta. */
  const runMutation = async (work, success) => {
    setBusy(true);
    try {
      const result = await work();
      mergeTeamState(result?.state);
      if (success) toast.success(typeof success === "function" ? success(result) : success);
      return result || true;
    } catch (error) {
      toast.error(getUniformErrorMessage(error));
      if (RELOAD_CODES.has(error?.response?.data?.code)) await loadMatrix();
      return null;
    } finally {
      setBusy(false);
    }
  };

  const submitEditor = async (form, { pendingPhoto = null } = {}) => {
    const { team, column, option, legacy } = editor;
    if (option) {
      const payload = buildUpdatePayload(option, form);
      if (!Object.keys(payload).length) return setEditor(null);
      const saved = await runMutation(() => updateUniformOption(team.idparticipant, option.id, payload), "Opción actualizada.");
      if (saved) setEditor(null);
      return undefined;
    }
    const payload = buildCreatePayload({ idtype: column.idtype, tone: column.tone, form, replaceLegacyIduniform: legacy?.iduniform });
    const saved = await runMutation(() => createUniformOption(team.idparticipant, payload), legacy ? "Registro legacy reemplazado." : "Opción creada.");
    if (!saved) return undefined;
    // La foto elegida durante el alta se sube en cuanto existe el idoption. Si falla, la opción y sus
    // colores ya están guardados: se avisa sin deshacerlos y la foto puede reintentarse desde "Editar".
    if (pendingPhoto && saved?.option?.id) {
      try {
        const updated = await uploadUniformOptionPhoto(team.idparticipant, saved.option.id, pendingPhoto);
        mergeOptionPhoto(team.idparticipant, updated.option);
        toast.success("Foto cargada.");
      } catch (error) {
        toast.error(getUniformErrorMessage(error, "La opción se guardó, pero no se pudo subir la foto."));
      }
    }
    setEditor(null);
    return undefined;
  };

  const removeOption = (team, option) => {
    if (!window.confirm(`¿Eliminar la opción “${option.label}”? Los partidos donde ya está asignada conservan su texto.`)) return;
    runMutation(
      () => deleteUniformOption(team.idparticipant, option.id),
      (result) => (result?.linkedMatches ? `Opción eliminada. ${result.linkedMatches} partido(s) conservan el texto.` : "Opción eliminada.")
    );
  };

  const moveOption = (team, column, option, direction) => {
    const optionIds = moveOptionInColumn(column, option.id, direction);
    if (!optionIds) return;
    runMutation(() => reorderUniformOptions(team.idparticipant, { idtype: column.idtype, tone: column.tone, optionIds }), "Orden actualizado.");
  };

  const columns = matrix?.teams?.[0]?.columns || [];
  const sportName = sports.find((sport) => String(sport.id) === String(idsport))?.name || matrix?.sport?.name || "";
  const counts = useMemo(() => {
    const result = { MISSING: 0, INCOMPLETE: 0, COMPLETE: 0, LEGACY: 0 };
    (matrix?.teams || []).forEach((team) => {
      result[team.status] = (result[team.status] || 0) + 1;
      if (team.hasLegacy) result.LEGACY += 1;
    });
    return result;
  }, [matrix]);

  const visibleTeams = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (matrix?.teams || []).filter((team) => {
      if (statusFilter === "LEGACY" ? !team.hasLegacy : statusFilter !== "all" && team.status !== statusFilter) return false;
      if (!term) return true;
      return [team.institution?.name, team.institution?.abrev].some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [matrix, search, statusFilter]);

  if (unavailable) {
    return <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">{unavailable}</div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">
          Evento
          <select value={idevent} onChange={(event) => setIdevent(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal">
            <option value="">Seleccione un evento</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
                {event.periodo ? ` (${event.periodo})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-slate-700">
          Deporte
          <select value={idsport} onChange={(event) => setIdsport(event.target.value)} disabled={!sports.length} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal disabled:bg-slate-100">
            <option value="">{idevent && !sports.length ? "Sin equipos inscritos" : "Seleccione un deporte"}</option>
            {sports.map((sport) => (
              <option key={sport.id} value={sport.id}>
                {sport.name || `Deporte ${sport.id}`}
              </option>
            ))}
          </select>
        </label>
      </div>

      {idevent && idsport && matrix && (
        <UniformDeadlinePanel
          idevent={idevent}
          idsport={idsport}
          sportName={sportName}
          teams={matrix.teams || []}
          summary={summary}
          baseDeadline={(summary?.sports || []).find((item) => String(item.idsport) === String(idsport))?.base || matrix.baseDeadline}
          onChanged={(nextSummary) => {
            setSummary(nextSummary);
            loadMatrix();
          }}
        />
      )}

      {loading && <div className="rounded-xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500">Cargando matriz de uniformes...</div>}

      {!loading && matrix && (
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-800">Opciones por equipo</h3>
              <p className="text-xs text-slate-500">
                Hasta {MAX_OPTIONS_PER_TYPE_TONE} alternativas por tipo y tono. Completo exige al menos una Camiseta activa,
                en cualquier tono; Arquero y Líbero son adicionales y opcionales. Colores solo del catálogo aprobado.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar institución"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
              />
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setStatusFilter(filter.id)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${statusFilter === filter.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                >
                  {filter.label}
                  {filter.id !== "all" ? ` (${counts[filter.id] || 0})` : ""}
                </button>
              ))}
              <button type="button" onClick={loadMatrix} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                <FiRefreshCw /> Recargar
              </button>
            </div>
          </div>

          <div ref={matrixScrollRef} className="mt-4 overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-max border-collapse text-sm">
              <thead className="sticky top-0 z-20 bg-slate-100">
                <tr>
                  <th className="sticky left-0 z-30 min-w-64 border-b border-r border-slate-200 bg-slate-100 px-3 py-2 text-left font-bold text-slate-700">Institución</th>
                  {columns.map((column) => (
                    <th key={`${column.idtype}:${column.tone}`} className="min-w-64 border-b border-slate-200 px-3 py-2 text-left font-bold text-slate-700">
                      {sectionLabel(column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleTeams.map((team) => {
                  const status = TEAM_STATUS[team.status] || TEAM_STATUS.MISSING;
                  const deadline = describeDeadline(team.deadline);
                  return (
                    <tr key={team.idparticipant} className="align-top">
                      <td className="sticky left-0 z-10 border-r border-slate-200 bg-white px-3 py-2">
                        <div className="flex items-start gap-2">
                          {team.institution?.logo ? (
                            <img src={team.institution.logo} alt="" className="h-8 w-8 rounded object-contain" />
                          ) : (
                            <span className="h-8 w-8 rounded bg-slate-100" />
                          )}
                          <div className="min-w-0">
                            <p className="font-bold text-slate-800">{team.institution?.abrev || "—"}</p>
                            <p className="max-w-48 truncate text-xs text-slate-500">{team.institution?.name || "Institución sin nombre"}</p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              <Badge tone={status.tone}>{status.label}</Badge>
                              {team.hasLegacy && <Badge tone="amber">Legacy</Badge>}
                              <Badge tone={deadline.open ? "emerald" : "rose"} title={deadline.text}>
                                {deadline.open ? "Envío abierto" : "Envío cerrado"}
                              </Badge>
                            </div>
                            {team.legacyOutsidePolicy?.length > 0 && (
                              <p className="mt-1 text-[11px] text-amber-700" title={team.legacyOutsidePolicy.map((row) => row.label || "Sin texto").join(" · ")}>
                                {team.legacyOutsidePolicy.length} registro(s) legacy fuera de la política (solo lectura)
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      {team.columns.map((column) => {
                        const firstEmpty = column.slots.find((slot) => slot.kind === "EMPTY")?.order;
                        const hasAnyOption = column.slots.some((slot) => slot.kind === "OPTION");
                        const filled = column.slots.filter((slot) => slot.kind !== "EMPTY").length;
                        const isFull = filled >= MAX_OPTIONS_PER_TYPE_TONE;
                        return (
                          <td key={`${column.idtype}:${column.tone}`} className="px-3 py-2">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wide text-slate-400">
                                <span>{filled}/{MAX_OPTIONS_PER_TYPE_TONE}</span>
                                {isFull && <span>Máximo {MAX_OPTIONS_PER_TYPE_TONE}</span>}
                              </div>
                              {column.slots.map((slot) => (
                                <SlotCell
                                  key={slot.order}
                                  team={team}
                                  column={column}
                                  slot={slot}
                                  isFirstEmpty={slot.order === firstEmpty}
                                  hasAnyOption={hasAnyOption}
                                  busy={busy}
                                  onCreate={(targetTeam, targetColumn) => setEditor({ team: targetTeam, column: targetColumn, option: null })}
                                  onEdit={(targetTeam, targetColumn, option) => setEditor({ team: targetTeam, column: targetColumn, option })}
                                  onReplace={(targetTeam, targetColumn, legacy) => setEditor({ team: targetTeam, column: targetColumn, option: null, legacy })}
                                  onDelete={removeOption}
                                  onMove={moveOption}
                                />
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {!visibleTeams.length && (
                  <tr>
                    <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-sm text-slate-500">
                      No hay equipos para el filtro seleccionado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* La barra nativa queda al final de la lista: esta la replica fija abajo mientras se trabaja arriba. */}
          <FloatingHorizontalScrollbar targetRef={matrixScrollRef} />
        </section>
      )}

      {editor && (
        <UniformOptionEditor
          key={`${editor.team.idparticipant}:${editor.column.idtype}:${editor.column.tone}:${editor.option?.id || editor.legacy?.iduniform || "new"}`}
          team={editor.team}
          column={editor.column}
          option={editor.option}
          legacy={editor.legacy}
          catalog={matrix?.catalog || []}
          saving={busy}
          onCancel={() => setEditor(null)}
          onSubmit={submitEditor}
          onOptionChanged={(updatedOption) => mergeOptionPhoto(editor.team.idparticipant, updatedOption)}
        />
      )}
    </div>
  );
};

export default CompanyUniformsTab;
